/**
 * Déclarer les semaines d'une période.
 *
 * ## Ce que cet écran remplace
 *
 * Les dates étaient tapées session par session : trois champs — début, fin,
 * nombre de jours — sur chacune des 76 sessions de l'été 2026. Soit ~1 000
 * saisies pour 66 faits réels, puisque le triplet (période, année, semaine)
 * détermine déjà les trois, et le fait sans une seule exception sur les 336
 * sessions mesurées.
 *
 * Ici, une semaine se déclare une fois. Les sessions en héritent.
 *
 * ## Le nombre de jours ne se tape pas
 *
 * Il se déduit du calendrier et de `public_holidays`. L'écran le DEMANDE à la
 * base (`programme_jours_ouvres`) plutôt que de le recalculer : deux copies
 * d'une règle finissent par différer, et c'est exactement ce qui est arrivé au
 * tarif local, réimplémenté dans deux magasins dont aucun ne testait
 * `tarif_conditions.active`.
 *
 * Il reste corrigeable à la main, pour la fermeture qu'aucun calendrier ne
 * connaît.
 */

import { useEffect, useState } from 'react';
import {
  CalendarDays,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  AlertTriangle,
  X,
} from 'lucide-react';
import {
  empriseDeLaSemaine,
  enregistrerSemaine,
  joursOuvres,
  supprimerSemaine,
  type Semaine,
} from '../../../../lib/programme/gateway.supabase';
import { messageDErreur, phraseDeJours } from '../../../../lib/programme/programmation';

interface Props {
  periodId: string;
  periodeLabel: string;
  annee: number;
  semaines: Semaine[];
  onChangement: () => void;
  onErreur: (message: string) => void;
}

interface Brouillon {
  id?: string;
  libelle: string;
  debut: string;
  fin: string;
  jours_reference: number;
  /** Vide : le calendrier décide. Renseigné : on le contredit. */
  jours_ouverts: string;
  remarques: string;
}

const VIDE: Brouillon = {
  libelle: '',
  debut: '',
  fin: '',
  jours_reference: 5,
  jours_ouverts: '',
  remarques: '',
};

/** Le vendredi de la semaine d'un lundi donné. */
function vendrediSuivant(lundi: string): string {
  if (!lundi) return '';
  const d = new Date(`${lundi}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + 4);
  return d.toISOString().slice(0, 10);
}

/** Le prochain libellé libre : S1, puis S2, puis S3. */
function prochainLibelle(semaines: Semaine[]): string {
  const pris = new Set(semaines.map((s) => s.libelle.toUpperCase()));
  for (let i = 1; i <= 30; i++) {
    if (!pris.has(`S${i}`)) return `S${i}`;
  }
  return '';
}

function dateCourte(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: '2-digit',
  });
}

const SemainesDeLaPeriode = ({
  periodId,
  periodeLabel,
  annee,
  semaines,
  onChangement,
  onErreur,
}: Props) => {
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [apercuJours, setApercuJours] = useState<number | null>(null);
  const [aSupprimer, setASupprimer] = useState<Semaine | null>(null);
  const [emprise, setEmprise] = useState<{ sessions: number; inscrits: number } | null>(null);

  /*
    L'aperçu du nombre de jours, demandé à la base à chaque changement de dates.
    Un aller-retour par frappe serait de trop : les dates viennent d'un
    `<input type="date">`, donc elles arrivent complètes ou pas du tout.
  */
  useEffect(() => {
    let vivant = true;
    if (!brouillon?.debut || !brouillon?.fin || brouillon.fin < brouillon.debut) {
      setApercuJours(null);
      return;
    }
    joursOuvres(brouillon.debut, brouillon.fin)
      .then((n) => {
        if (vivant) setApercuJours(n);
      })
      .catch(() => {
        if (vivant) setApercuJours(null);
      });
    return () => {
      vivant = false;
    };
  }, [brouillon?.debut, brouillon?.fin]);

  const ouvrirNouveau = () =>
    setBrouillon({ ...VIDE, libelle: prochainLibelle(semaines) });

  const ouvrirModification = (s: Semaine) =>
    setBrouillon({
      id: s.id,
      libelle: s.libelle,
      debut: s.debut,
      fin: s.fin,
      jours_reference: s.jours_reference,
      jours_ouverts: '',
      remarques: s.remarques ?? '',
    });

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brouillon) return;
    setEnregistrement(true);
    try {
      await enregistrerSemaine({
        id: brouillon.id,
        period_id: periodId,
        annee,
        libelle: brouillon.libelle,
        debut: brouillon.debut,
        fin: brouillon.fin,
        jours_reference: brouillon.jours_reference,
        jours_ouverts: brouillon.jours_ouverts
          ? Number(brouillon.jours_ouverts)
          : null,
        remarques: brouillon.remarques,
      });
      setBrouillon(null);
      onChangement();
    } catch (err: unknown) {
      onErreur(messageDErreur(err, 'La semaine n’a pas pu être enregistrée.'));
    } finally {
      setEnregistrement(false);
    }
  };

  /*
    Avant de proposer la suppression, on va voir ce que la semaine porte.
    `sessions.semaine_id` est en ON DELETE SET NULL : les sessions survivent
    mais perdent leur rattachement, donc la grille ne les reconnaîtrait plus et
    un nouveau passage en créerait de nouvelles. Ça doit être dit avant, pas
    découvert après.
  */
  const demanderSuppression = async (s: Semaine) => {
    setASupprimer(s);
    setEmprise(null);
    try {
      setEmprise(await empriseDeLaSemaine(s.id));
    } catch {
      setEmprise(null);
    }
  };

  const confirmerSuppression = async () => {
    if (!aSupprimer) return;
    try {
      await supprimerSemaine(aSupprimer.id);
      setASupprimer(null);
      onChangement();
    } catch (err: unknown) {
      onErreur(messageDErreur(err, 'La semaine n’a pas pu être supprimée.'));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary-600" />
            Les semaines de {periodeLabel} {annee}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Déclarées une fois. Les sessions en héritent leurs dates et leur nombre de
            jours — le tarif d’une semaine écourtée s’en déduit.
          </p>
        </div>
        <button onClick={ouvrirNouveau} className="btn-outline flex items-center shrink-0">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une semaine
        </button>
      </div>

      {semaines.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Aucune semaine déclarée pour {periodeLabel} {annee}.
          <br />
          Commencez par ici : sans semaine, il n’y a rien à programmer.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Semaine
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Du — au
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Jours ouvrés
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Remarques
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {semaines.map((s) => {
                const ecourtee = s.jours_ouverts < s.jours_reference;
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{s.libelle}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {dateCourte(s.debut)} — {dateCourte(s.fin)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          ecourtee
                            ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800'
                            : 'text-gray-600'
                        }
                      >
                        {phraseDeJours(s.jours_ouverts, s.jours_reference)}
                        {ecourtee && ' · tarif proratisé'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500 max-w-[240px] truncate">
                      {s.remarques || '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={() => ouvrirModification(s)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Modifier"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => demanderSuppression(s)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Le formulaire d'une semaine --- */}
      {brouillon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {brouillon.id ? 'Modifier la semaine' : 'Nouvelle semaine'}
                <span className="text-gray-500 font-normal">
                  {' '}
                  — {periodeLabel} {annee}
                </span>
              </h3>
              <button onClick={() => setBrouillon(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={enregistrer} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Libellé</label>
                  <input
                    type="text"
                    value={brouillon.libelle}
                    onChange={(e) => setBrouillon({ ...brouillon, libelle: e.target.value })}
                    className="form-input"
                    placeholder="S1"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Premier jour</label>
                  <input
                    type="date"
                    value={brouillon.debut}
                    onChange={(e) =>
                      setBrouillon({
                        ...brouillon,
                        debut: e.target.value,
                        /*
                          Le vendredi est proposé, jamais imposé : un séjour de
                          dix jours se saisit en changeant la date de fin.
                        */
                        fin: brouillon.fin || vendrediSuivant(e.target.value),
                      })
                    }
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Dernier jour</label>
                  <input
                    type="date"
                    value={brouillon.fin}
                    onChange={(e) => setBrouillon({ ...brouillon, fin: e.target.value })}
                    className="form-input"
                    min={brouillon.debut || undefined}
                    required
                  />
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                {apercuJours === null ? (
                  <span className="text-gray-500">
                    Renseignez les deux dates : le nombre de jours ouvrés sera calculé.
                  </span>
                ) : (
                  <span className="text-gray-700">
                    <strong>{apercuJours}</strong> jour{apercuJours > 1 ? 's' : ''} ouvré
                    {apercuJours > 1 ? 's' : ''} — week-ends et jours fériés déduits.
                    {apercuJours < brouillon.jours_reference && (
                      <span className="text-amber-700">
                        {' '}
                        Le tarif normal sera proratisé ({apercuJours}/
                        {brouillon.jours_reference}) ; le réduit et le local ne changent pas.
                      </span>
                    )}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">
                    Jours d’une semaine pleine
                    <span className="text-gray-400 font-normal"> (dénominateur)</span>
                  </label>
                  <input
                    type="number"
                    value={brouillon.jours_reference}
                    onChange={(e) =>
                      setBrouillon({ ...brouillon, jours_reference: Number(e.target.value) })
                    }
                    className="form-input"
                    min={1}
                    max={23}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">
                    Forcer les jours ouvrés
                    <span className="text-gray-400 font-normal"> (facultatif)</span>
                  </label>
                  <input
                    type="number"
                    value={brouillon.jours_ouverts}
                    onChange={(e) =>
                      setBrouillon({ ...brouillon, jours_ouverts: e.target.value })
                    }
                    className="form-input"
                    min={1}
                    max={23}
                    placeholder={apercuJours !== null ? String(apercuJours) : 'calculé'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Pour une fermeture qu’aucun calendrier ne connaît.
                  </p>
                </div>
              </div>

              <div>
                <label className="form-label">Remarques</label>
                <input
                  type="text"
                  value={brouillon.remarques}
                  onChange={(e) => setBrouillon({ ...brouillon, remarques: e.target.value })}
                  className="form-input"
                  placeholder="Fête nationale le 21, piscine fermée…"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setBrouillon(null)} className="btn-outline">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex items-center" disabled={enregistrement}>
                  {enregistrement && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- La confirmation de suppression, qui dit ce qu'elle emporte --- */}
      {aSupprimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 rounded-full p-2 mr-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold">
                Supprimer la semaine {aSupprimer.libelle} ?
              </h3>
            </div>

            {emprise === null ? (
              <p className="text-gray-500 text-sm mb-6 flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Je regarde ce que cette semaine porte…
              </p>
            ) : emprise.sessions === 0 ? (
              <p className="text-gray-600 mb-6">
                Cette semaine ne porte aucune session. Sa suppression n’emporte rien.
              </p>
            ) : (
              <div className="mb-6 space-y-3 text-sm">
                <p className="text-gray-700">
                  Cette semaine porte <strong>{emprise.sessions} session(s)</strong>
                  {emprise.inscrits > 0 && (
                    <>
                      {' '}
                      et <strong>{emprise.inscrits} inscription(s)</strong>
                    </>
                  )}
                  .
                </p>
                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Les sessions ne seront pas supprimées : elles gardent leurs dates et leurs
                  inscriptions. Mais elles perdront leur rattachement, donc la grille ne les
                  reconnaîtra plus — et un nouveau passage en créerait de nouvelles, en double.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={() => setASupprimer(null)} className="btn-outline">
                Annuler
              </button>
              <button
                onClick={confirmerSuppression}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SemainesDeLaPeriode;
